import { MenuIcon } from '@playerbase/ui';

import { useShell } from '../use-shell.js';
import { GameSwitcher } from '../sidebar/game-switcher.js';
import { BuildPill } from './build-pill.js';
import { MilestoneSelector } from './milestone-selector.js';
import { PBMark } from './pb-mark.js';
import { ThemeToggle } from './theme-toggle.js';
import { TopbarActions } from './topbar-actions.js';

type TopbarProps = {
  onOpenDrawer: () => void;
};

// Topbar holds the PB mark, the game switcher, the per-game build /
// milestone strip, and the right-hand actions + theme toggle. The
// screen heading lives on the secondary rail instead.
export function Topbar({ onOpenDrawer }: TopbarProps): JSX.Element {
  const { activeGame } = useShell();

  return (
    <header className="flex shrink-0 flex-col border-b border-border bg-surface md:flex-row md:items-center md:gap-3 md:pl-3 md:pr-4">
      {/* Stable 48px row that owns the left-hand controls and the
          right-hand cluster. flex-1 + min-w-0 so the row fills the
          header on desktop, letting ml-auto push the right group to
          the far edge. */}
      <div className="flex h-12 items-center gap-3 px-3 md:min-w-0 md:flex-1 md:px-0">
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
          className="md:hidden flex h-8 w-8 items-center justify-center rounded-md border border-border-strong text-text-muted transition-colors hover:bg-surface-2 hover:text-text"
        >
          <MenuIcon />
        </button>
        <PBMark />

        {/* Desktop: GameSwitcher sits left of the build/milestone strip.
            On mobile the switcher drops to the second row below. */}
        {activeGame !== null && (
          // Extra md:ml-2 buys a little breathing room between the PB
          // mark and the game switcher so they don't feel squashed.
          <div className="hidden md:ml-2 md:flex items-center gap-2">
            <GameSwitcher />
            <BuildPill />
            <MilestoneSelector />
          </div>
        )}

        <div className="ml-auto flex items-center gap-2 md:hidden">
          <TopbarActions compact />
          <ThemeToggle />
        </div>
        <div className="ml-auto hidden md:flex items-center gap-2">
          <TopbarActions />
          <ThemeToggle />
        </div>
      </div>

      {/* Context strip wraps onto a second row below md so the game
          switcher, build, and milestone stay reachable on narrow viewports. */}
      {activeGame !== null && (
        <div className="md:hidden flex items-center gap-2 overflow-x-auto border-t border-border px-3 py-2">
          <GameSwitcher />
          <BuildPill />
          <MilestoneSelector />
        </div>
      )}
    </header>
  );
}
